const TAX_ID_DIGIT_LENGTH = 9;
export const TAX_ID_FORMATTED_LENGTH = 10;
const TAX_ID_PREFIX_LENGTH = 2;

/** Allows only digits and dash; shape is enforced by normalizeTaxId. */
export const TAX_ID_INPUT_PATTERN = /^[\d-]*$/;

const digitsOf = (value: string) => value.replace(/\D/g, "").slice(0, TAX_ID_DIGIT_LENGTH);

export const normalizeTaxId = (taxId: string | null | undefined): string => {
  const digits = digitsOf(taxId ?? "");

  if (digits.length < TAX_ID_PREFIX_LENGTH) {
    return digits;
  }

  return `${digits.slice(0, TAX_ID_PREFIX_LENGTH)}-${digits.slice(TAX_ID_PREFIX_LENGTH)}`;
};

export const normalizeTaxIdInput = (
  taxId: string | null | undefined,
  previousTaxId: string | null | undefined,
): string => {
  const next = taxId ?? "";
  const previous = previousTaxId ?? "";
  const previousDigits = digitsOf(previous);

  if (previous.includes("-") && !next.includes("-") && previousDigits === digitsOf(next)) {
    const withoutPrefixDigit =
      previousDigits.length <= TAX_ID_PREFIX_LENGTH ?
        previousDigits.slice(0, -1)
      : previousDigits.slice(0, TAX_ID_PREFIX_LENGTH - 1) +
        previousDigits.slice(TAX_ID_PREFIX_LENGTH);

    return normalizeTaxId(withoutPrefixDigit);
  }

  return normalizeTaxId(next);
};

export const getTaxIdCaretIndex = (
  rawValue: string,
  caretInRaw: number,
  previousValue: string,
  formattedValue: string,
): number => {
  const rawDigits = digitsOf(rawValue);
  const previousDigits = digitsOf(previousValue);
  const deletedDigitOnDashBackspace =
    rawDigits.length === previousDigits.length &&
    digitsOf(formattedValue).length === previousDigits.length - 1;

  let digitCount = digitsOf(rawValue.slice(0, Math.max(0, caretInRaw))).length;

  if (deletedDigitOnDashBackspace) {
    digitCount = Math.max(0, digitCount - 1);
  }

  if (digitCount <= 0) {
    return 0;
  }

  let caret = formattedValue.length;
  let seen = 0;

  for (let i = 0; i < formattedValue.length; i++) {
    if (formattedValue[i] < "0" || formattedValue[i] > "9") {
      continue;
    }

    seen += 1;

    if (seen === digitCount) {
      caret = i + 1;
      break;
    }
  }

  const hasTrailingDashOnly =
    formattedValue[caret] === "-" && !/\d/.test(formattedValue.slice(caret + 1));

  return hasTrailingDashOnly ? caret + 1 : caret;
};

export const isValidTaxId = (taxId: string | null | undefined): boolean => {
  const digits = digitsOf(taxId ?? "");

  return !digits || digits.length === TAX_ID_DIGIT_LENGTH;
};
